import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Printer, ArrowLeft, Loader2, FileSpreadsheet, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Dealership-style colour key (matches the manual highlighter system on the
 * old printed diary):
 *   🟢 GREEN  = wait — customer waiting at the dealership
 *   🔴 RED    = collection / deliver — collect & deliver / pick-up + drop-off
 *   🟡 YELLOW = loan-car job
 *   🟣 PURPLE = diagnostic
 *   ⚪ White   = standard drop-off (no special handling)
 */
function rowTint(j) {
  // Priority: wait > collect/deliver > loan > diagnostic
  // (so a "wait" with a loan reads as wait — that's what the customer's experience is)
  const t = (j.job_type || "").toLowerCase();
  if (t === "wait") return "tint-wait";
  if (t === "collection" || t === "delivery") return "tint-collect";
  if (j.loan_car_provided || j.loan_car) return "tint-loan";
  if (t === "diagnostic") return "tint-diag";
  return "tint-standard";
}

function jobBadge(j) {
  const t = (j.job_type || "").toLowerCase();
  if (t === "wait") return { label: "WAITING", cls: "bg-green-700 text-white" };
  if (t === "collection") return { label: "COLLECT & DELIVER", cls: "bg-red-700 text-white" };
  if (t === "delivery") return { label: "DELIVER", cls: "bg-red-700 text-white" };
  if (j.loan_car_provided || j.loan_car) return { label: "LOAN CAR", cls: "bg-yellow-500 text-black" };
  if (t === "diagnostic") return { label: "DIAGNOSTIC", cls: "bg-purple-700 text-white" };
  return null;
}

function fmtTime(t) {
  if (!t) return "—";
  const m = /^(\d{1,2})[.:](\d{2})/.exec(String(t).trim());
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return t;
}

function fmtDateLong(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}

const PRINT_PREFS_KEY = "ssp.diary.print.prefs";
const PAPERS = [
  { value: "A4", label: "A4" },
  { value: "A3", label: "A3" },
  { value: "Letter", label: "Letter" },
  { value: "Legal", label: "Legal" },
];

function loadPrefs() {
  try {
    const p = JSON.parse(localStorage.getItem(PRINT_PREFS_KEY) || "{}");
    return {
      orientation: p.orientation === "portrait" ? "portrait" : "landscape",
      paper: PAPERS.some((x) => x.value === p.paper) ? p.paper : "A4",
      scale: Number(p.scale) >= 60 && Number(p.scale) <= 140 ? Number(p.scale) : 100,
    };
  } catch {
    return { orientation: "landscape", paper: "A4", scale: 100 };
  }
}

export default function PrintableDiaryPage() {
  const [params] = useSearchParams();
  const dateParam = params.get("date");
  const [date, setDate] = useState(dateParam || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workshop, setWorkshop] = useState("");
  const [prefs, setPrefs] = useState(loadPrefs);
  const [exporting, setExporting] = useState(false);

  const setPref = (patch) =>
    setPrefs((p) => {
      const next = { ...p, ...patch };
      try { localStorage.setItem(PRINT_PREFS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });

  const exportExcel = async () => {
    setExporting(true);
    try {
      const q = dateParam ? `?date=${dateParam}` : "";
      const r = await api.get(`/pre-calls/export.xlsx${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(
        new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `run-sheet-${date || "today"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel run sheet downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build the Excel sheet");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const q = dateParam ? `?date=${dateParam}` : "";
        const r = await api.get(`/pre-calls${q}`);
        setRows(r.data.appointments || []);
        setDate(r.data.date || dateParam || "");
        try {
          const me = await api.get("/auth/me");
          setWorkshop(me.data?.workshop_name || me.data?.name || "");
        } catch { /* ignore */ }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load diary");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateParam]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (a.appointment_time || "").localeCompare(b.appointment_time || "")),
    [rows]
  );

  // Tally counts per category for the header strip
  const tallies = useMemo(() => {
    const t = { wait: 0, collect: 0, loan: 0, diag: 0, standard: 0 };
    sorted.forEach((j) => {
      const k = rowTint(j).replace("tint-", "");
      t[k] = (t[k] || 0) + 1;
    });
    return t;
  }, [sorted]);

  return (
    <>
      <style>{`
        /* Tints — use hex so the print toggle reliably keeps them */
        .tint-wait     { background:#dcfce7; border-left:6px solid #15803d; }   /* green */
        .tint-collect  { background:#fee2e2; border-left:6px solid #b91c1c; }   /* red */
        .tint-loan     { background:#fef9c3; border-left:6px solid #ca8a04; }   /* yellow */
        .tint-diag     { background:#f3e8ff; border-left:6px solid #7e22ce; }   /* purple */
        .tint-standard { background:#ffffff; border-left:6px solid #d4d4d8; }
        @media print {
          @page { size: ${prefs.paper} ${prefs.orientation}; margin: 8mm; }
          html, body { background:#fff !important; }
          .no-print { display:none !important; }
          .printable-sheet { box-shadow:none !important; border:0 !important; }
          .row-tint { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table { page-break-inside:auto; }
          tr { page-break-inside:avoid; page-break-after:auto; }
          thead { display:table-header-group; }
        }
      `}</style>

      <div className="min-h-screen bg-zinc-100 py-6 px-3 print:bg-white print:py-0 print:px-0">
        {/* Top toolbar (hidden when printing) */}
        <div className="max-w-[1400px] mx-auto mb-4 flex flex-wrap items-center justify-between gap-3 no-print">
          <Link to="/pre-calls" className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to Pre-Calls
          </Link>

          <div className="flex flex-wrap items-center gap-2">
            {/* Orientation */}
            <div className="inline-flex rounded-md border border-zinc-300 bg-white overflow-hidden" data-testid="print-orientation-group">
              {["landscape", "portrait"].map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setPref({ orientation: o })}
                  data-testid={`print-orientation-${o}`}
                  className={`px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                    prefs.orientation === o ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"
                  }`}
                >
                  {o}
                </button>
              ))}
            </div>

            {/* Paper size */}
            <select
              value={prefs.paper}
              onChange={(e) => setPref({ paper: e.target.value })}
              data-testid="print-paper-select"
              className="px-2 py-1.5 rounded-md border border-zinc-300 bg-white text-[11px] font-bold text-zinc-700"
              title="Paper size"
            >
              {PAPERS.map((p) => (
                <option key={p.value} value={p.value}>{p.value}</option>
              ))}
            </select>

            {/* Scale */}
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md border border-zinc-300 bg-white">
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Size</span>
              <input
                type="range"
                min="60"
                max="140"
                step="5"
                value={prefs.scale}
                onChange={(e) => setPref({ scale: Number(e.target.value) })}
                data-testid="print-scale-slider"
                className="w-28 accent-zinc-900"
                title="Shrink or enlarge the sheet"
              />
              <span className="font-mono text-[11px] font-bold text-zinc-800 w-9 text-right" data-testid="print-scale-value">
                {prefs.scale}%
              </span>
              {prefs.scale !== 100 && (
                <button
                  type="button"
                  onClick={() => setPref({ scale: 100 })}
                  data-testid="print-scale-reset"
                  className="text-zinc-400 hover:text-zinc-800"
                  title="Reset to 100%"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <button
              onClick={exportExcel}
              disabled={exporting || loading}
              className="btn-outline text-sm disabled:opacity-50"
              data-testid="printable-excel-btn"
              title="Download as an Excel workbook"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Excel
            </button>

            <button
              onClick={() => window.print()}
              className="btn-primary text-sm"
              data-testid="printable-print-btn"
            >
              <Printer className="w-4 h-4" /> Print sheet
            </button>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto mb-3 text-[11px] text-zinc-500 no-print">
          Tip: in the Windows print dialog choose <strong>More settings → Background graphics</strong> so the colour tints print, and set
          Layout to <strong>{prefs.orientation === "landscape" ? "Landscape" : "Portrait"}</strong> if it hasn't picked it up automatically.
        </div>

        {/* The sheet */}
        <div
          className="max-w-[1400px] mx-auto bg-white rounded-lg shadow-xl border border-zinc-300 printable-sheet print:rounded-none print:border-0 print:max-w-none"
          style={{ zoom: prefs.scale / 100 }}
          data-testid="printable-sheet"
        >
          {/* Header */}
          <div className="px-5 py-3 border-b-2 border-zinc-700 flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Daily run sheet · Service Advisor</div>
              <div className="font-display text-2xl font-black tracking-tight text-zinc-900">
                {fmtDateLong(date) || "—"}
              </div>
            </div>
            {workshop && (
              <div className="text-right text-xs text-zinc-600">
                <div className="font-semibold text-zinc-800">{workshop}</div>
                <div className="text-[10px] uppercase tracking-wider">{sorted.length} appointments</div>
              </div>
            )}
          </div>

          {/* Tally strip */}
          {!loading && sorted.length > 0 && (
            <div className="px-5 py-2 border-b border-zinc-300 flex flex-wrap items-center gap-3 text-[11px] text-zinc-700 row-tint">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded tint-wait row-tint font-semibold"><span className="font-mono text-zinc-900">{tallies.wait || 0}</span> waiting</span>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded tint-collect row-tint font-semibold"><span className="font-mono text-zinc-900">{tallies.collect || 0}</span> collect &amp; deliver</span>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded tint-loan row-tint font-semibold"><span className="font-mono text-zinc-900">{tallies.loan || 0}</span> loan-car</span>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded tint-diag row-tint font-semibold"><span className="font-mono text-zinc-900">{tallies.diag || 0}</span> diagnostic</span>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded tint-standard row-tint font-semibold border border-zinc-300"><span className="font-mono text-zinc-900">{tallies.standard || 0}</span> drop-off</span>
              <span className="ml-auto font-mono text-[10px] text-zinc-500">Printed {new Date().toLocaleString("en-GB")}</span>
            </div>
          )}

          {loading ? (
            <div className="p-10 text-center text-zinc-500 inline-flex items-center justify-center gap-2 w-full"><Loader2 className="w-4 h-4 animate-spin" /> Loading diary…</div>
          ) : sorted.length === 0 ? (
            <div className="p-10 text-center text-zinc-500">No bookings for this date.</div>
          ) : (
            <table className="w-full text-[11px] leading-tight" data-testid="printable-table">
              <thead className="bg-zinc-900 text-white">
                <tr>
                  <th className="text-left px-2 py-1.5 w-[58px]">Time In</th>
                  <th className="text-left px-2 py-1.5 w-[58px]">Time Out</th>
                  <th className="text-left px-2 py-1.5 w-[110px]">Type</th>
                  <th className="text-left px-2 py-1.5 w-[60px]">WIP</th>
                  <th className="text-left px-2 py-1.5 w-[90px]">Reg</th>
                  <th className="text-left px-2 py-1.5 w-[170px]">Customer</th>
                  <th className="text-left px-2 py-1.5">Work</th>
                  <th className="text-left px-2 py-1.5 w-[140px]" title="Customer Magic Points — drink & concierge flags">CMPs</th>
                  <th className="text-center px-2 py-1.5 w-[42px]">VHC</th>
                  <th className="text-center px-2 py-1.5 w-[60px]">Updated</th>
                  <th className="text-center px-2 py-1.5 w-[60px]">Invoiced</th>
                  <th className="text-center px-2 py-1.5 w-[60px]">Collected</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((j) => {
                  const tint = rowTint(j);
                  const badge = jobBadge(j);
                  return (
                    <tr key={j.id} className={`${tint} row-tint border-b border-zinc-300`} data-testid={`printable-row-${j.id}`}>
                      <td className="px-2 py-1.5 font-mono font-bold text-zinc-900 whitespace-nowrap">{fmtTime(j.appointment_time)}</td>
                      <td className="px-2 py-1.5 font-mono text-zinc-900 whitespace-nowrap">{fmtTime(j.appointment_out_time)}</td>
                      <td className="px-2 py-1.5">
                        {badge ? (
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-mono font-bold tracking-wider ${badge.cls}`}>{badge.label}</span>
                        ) : (
                          <span className="text-[9px] font-mono uppercase text-zinc-500">drop-off</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-zinc-800">{j.wip_number || ""}</td>
                      <td className="px-2 py-1.5 font-mono font-bold uppercase text-zinc-900">{j.vehicle_reg || ""}</td>
                      <td className="px-2 py-1.5 text-zinc-900 font-semibold truncate">{j.customer_name || ""}</td>
                      <td className="px-2 py-1.5 text-zinc-800">
                        {j.work_description || (j.service_type ? j.service_type.toUpperCase() : "")}
                        {j.campaign_number && <span className="ml-2 text-[10px] font-mono text-zinc-600">[{j.campaign_number}]</span>}
                        {j.loan_car && <span className="ml-2 text-[10px] font-mono text-zinc-600">🔑 {j.loan_car}</span>}
                        {j.order_status && /unconfirmed/i.test(j.order_status) && <span className="ml-2 text-[10px] font-bold uppercase text-red-700">UNCONFIRMED</span>}
                      </td>
                      {/*
                        Customer Magic Points — drink first (biggest wow),
                        then compact letter-chips for allergies (A), pet (P)
                        and "Always valet" (V). Blank cell = no CMPs on file.
                      */}
                      <td className="px-2 py-1.5 text-zinc-800" data-testid={`printable-cmp-${j.id}`}>
                        {j.cmp_hot_drink && (
                          <div className="text-[10px] font-semibold text-amber-800 leading-tight">☕ {j.cmp_hot_drink}</div>
                        )}
                        <div className="mt-0.5 flex flex-wrap gap-0.5">
                          {j.cmp_allergies && (
                            <span
                              className="inline-block px-1 py-0.5 rounded text-[9px] font-mono font-bold bg-red-100 text-red-800 border border-red-300"
                              title={`Allergies: ${j.cmp_allergies}`}
                            >A</span>
                          )}
                          {j.cmp_pet_at_home && (
                            <span
                              className="inline-block px-1 py-0.5 rounded text-[9px] font-mono font-bold bg-amber-100 text-amber-800 border border-amber-300"
                              title={`Pet at home: ${j.cmp_pet_at_home}`}
                            >P</span>
                          )}
                          {j.cmp_valet_preference === "always" && (
                            <span
                              className="inline-block px-1 py-0.5 rounded text-[9px] font-mono font-bold bg-cyan-100 text-cyan-800 border border-cyan-300"
                              title="Always valet"
                            >V</span>
                          )}
                          {j.cmp_valet_preference === "never" && (
                            <span
                              className="inline-block px-1 py-0.5 rounded text-[9px] font-mono font-bold bg-zinc-100 text-zinc-700 border border-zinc-300"
                              title="No valet — customer preference"
                            >V✗</span>
                          )}
                        </div>
                      </td>
                      {/* Empty boxes — advisor ticks by hand */}
                      <td className="px-2 py-1.5 border-l border-zinc-400/40"></td>
                      <td className="px-2 py-1.5 border-l border-zinc-400/40"></td>
                      <td className="px-2 py-1.5 border-l border-zinc-400/40"></td>
                      <td className="px-2 py-1.5 border-l border-zinc-400/40"></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Colour key */}
          <div className="px-5 py-2 border-t border-zinc-300 flex flex-wrap items-center gap-4 text-[10px] text-zinc-700">
            <span className="font-mono uppercase tracking-wider text-zinc-500">Key:</span>
            <div className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-4 tint-wait row-tint rounded-sm border border-green-700" />Waiting</div>
            <div className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-4 tint-collect row-tint rounded-sm border border-red-700" />Collect &amp; deliver</div>
            <div className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-4 tint-loan row-tint rounded-sm border border-yellow-600" />Loan car</div>
            <div className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-4 tint-diag row-tint rounded-sm border border-purple-700" />Diagnostic</div>
            <div className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-4 tint-standard row-tint rounded-sm border border-zinc-400" />Standard drop-off</div>
            <span className="inline-flex items-center gap-1.5 ml-2 pl-3 border-l border-zinc-300">
              <span className="font-mono uppercase tracking-wider text-zinc-500">CMPs:</span>
              <span className="inline-block px-1 py-0.5 rounded text-[9px] font-mono font-bold bg-red-100 text-red-800 border border-red-300">A</span>Allergy
              <span className="inline-block px-1 py-0.5 rounded text-[9px] font-mono font-bold bg-amber-100 text-amber-800 border border-amber-300 ml-1">P</span>Pet
              <span className="inline-block px-1 py-0.5 rounded text-[9px] font-mono font-bold bg-cyan-100 text-cyan-800 border border-cyan-300 ml-1">V</span>Always valet
            </span>
            <span className="ml-auto font-mono text-[10px] text-zinc-500">ServiceSell PRO&trade;</span>
          </div>
        </div>
      </div>
    </>
  );
}
